import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { EmptyState, ErrorState, Skeleton } from "../../../../shared-ui/States";
import { ModulePage, ModuleCard, StatusBadge } from "../../../../shared-ui/ModulePage";
import "../../../../shared-ui/crane-tokens.css";

import { getAssetServiceBaseUrl } from "../../../../config/apiBase";

const AUTH_TOKEN_KEY = "zTaskAuthToken";
const ASSET_API_URL = `${getAssetServiceBaseUrl()}/api`;
const MAINT_BASE =
  process.env.REACT_APP_MAINTENANCE_API_URL ||
  process.env.REACT_APP_MAINTENANCE_SERVICE_BASE_URL ||
  process.env.REACT_APP_MAINT_BASE_URL ||
  "http://127.0.0.1:3010";

const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}`,
  Accept: "application/json",
});

function maintTicketsUrl() {
  const base = String(MAINT_BASE || "").replace(/\/$/, "");
  if (!base) return "";
  if (base.endsWith("/api")) return `${base}/master-tickets`;
  if (base.includes("/api/")) return `${base.replace(/\/$/, "")}/master-tickets`;
  return `${base}/api/master-tickets`;
}

/**
 * Asset details + maintenance lifecycle — REAL asset + optional maintenance tickets only.
 * Does not invent timeline events.
 */
export default function AssetLifecyclePage() {
  const { id } = useParams();
  const [asset, setAsset] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      setErr("");
      try {
        const res = await fetch(`${ASSET_API_URL}/assets/${id}`, { headers: headers() });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.message || `HTTP ${res.status}`);
        if (!cancelled) setAsset(body);

        if (MAINT_BASE) {
          try {
            const tRes = await fetch(maintTicketsUrl(), {
              headers: headers(),
            });
            const tBody = await tRes.json().catch(() => []);
            const list = Array.isArray(tBody) ? tBody : tBody?.data || tBody?.tickets || [];
            const assetCode = body?.asset_code;
            const related = list.filter((t) => {
              const aid = t.asset_id ?? t.assetId ?? t.asset?.id;
              if (aid != null && String(aid) === String(id)) return true;
              if (assetCode && t.asset_code) {
                return String(t.asset_code).toLowerCase() === String(assetCode).toLowerCase();
              }
              return false;
            });
            if (!cancelled) setTickets(related);
          } catch {
            if (!cancelled) setTickets([]);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e.message);
          setAsset(null);
          setTickets([]);
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const timeline = [];
  if (asset) {
    if (asset.created_at || asset.add_date || asset.createdAt) {
      timeline.push({
        when: asset.created_at || asset.add_date || asset.createdAt,
        label: "Asset registered",
        detail: asset.asset_code || asset.name || `#${asset.id}`,
      });
    }
    if (asset.updated_at || asset.update_date || asset.updatedAt) {
      timeline.push({
        when: asset.updated_at || asset.update_date || asset.updatedAt,
        label: "Last updated",
        detail: asset.status || asset.asset_status || "",
      });
    }
  }
  tickets.forEach((t) => {
    timeline.push({
      when: t.created_at || t.createdAt || t.opened_at || t.updated_at,
      label: `Maintenance: ${t.status || t.state || "ticket"}`,
      detail: t.title || t.ticket_no || t.id,
    });
  });
  timeline.sort((a, b) => new Date(b.when || 0) - new Date(a.when || 0));

  return (
    <ModulePage
      crumbs={["Operations", "ZAsset", "Asset Lifecycle"]}
      title={asset?.name || asset?.asset_code || "Asset details"}
      description="Asset passport and maintenance lifecycle from live asset + maintenance APIs."
      actions={
        <div style={{ display: "flex", gap: 8 }}>
          <Link to="/assets" className="crane-btn">
            Back to register
          </Link>
          <Link to={`/assets/${id}/edit`} className="crane-btn crane-btn--primary">
            Edit asset
          </Link>
        </div>
      }
    >
      {busy && <Skeleton rows={5} />}
      {err && <ErrorState title="Asset unavailable" description={err} />}
      {!busy && asset && (
        <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
          <ModuleCard title="Asset passport">
            <table className="crane-table">
              <tbody>
                <tr>
                  <td>Code</td>
                  <td>{asset.asset_code || asset.code || "—"}</td>
                </tr>
                <tr>
                  <td>Name</td>
                  <td>{asset.name || "—"}</td>
                </tr>
                <tr>
                  <td>Status</td>
                  <td>
                    <StatusBadge>{asset.status || asset.asset_status || "—"}</StatusBadge>
                  </td>
                </tr>
                <tr>
                  <td>Serial</td>
                  <td>{asset.serial_no || asset.serial_number || "—"}</td>
                </tr>
                <tr>
                  <td>Customer</td>
                  <td>{asset.customer_name || asset.cust_id || "—"}</td>
                </tr>
                <tr>
                  <td>Location</td>
                  <td>{asset.location || asset.site || asset.location_name || "—"}</td>
                </tr>
              </tbody>
            </table>
          </ModuleCard>
          <ModuleCard title="Maintenance lifecycle" badge={<span className="crane-badge">{timeline.length}</span>}>
            {timeline.length === 0 ? (
              <EmptyState
                title="No lifecycle events yet"
                description="Events appear from asset timestamps and related maintenance tickets when available."
              />
            ) : (
              <ul style={{ listStyle: "none", margin: 0, padding: 12 }}>
                {timeline.map((ev, i) => (
                  <li
                    key={`${ev.when}-${i}`}
                    style={{
                      borderLeft: "2px solid var(--crane-primary)",
                      padding: "8px 12px",
                      marginBottom: 8,
                    }}
                  >
                    <div style={{ fontSize: 11, color: "var(--crane-on-surface-variant)" }}>
                      {ev.when ? String(ev.when).replace("T", " ").slice(0, 19) : "—"}
                    </div>
                    <div style={{ fontWeight: 600 }}>{ev.label}</div>
                    {ev.detail && <div style={{ fontSize: 13 }}>{ev.detail}</div>}
                  </li>
                ))}
              </ul>
            )}
          </ModuleCard>
        </div>
      )}
    </ModulePage>
  );
}
